import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { LEAD_STATUS, LEAD_STATUS_ORDER } from "../../lib/b2b";
import { Radio, ArrowLeft, Download, Search, Users, CalendarCheck, FileText, PenLine, ChevronRight } from "lucide-react";

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <p className="text-2xl font-extrabold text-slate-900">{value}</p>
    <p className="text-xs font-semibold text-slate-500">{label}</p>
  </div>
);

export default function AdminLeads() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    const [l, s] = await Promise.all([api.get("/admin/b2b/leads", { params }), api.get("/admin/b2b/stats")]);
    setLeads(l.data); setStats(s.data);
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const res = await api.get("/admin/b2b/leads/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "prontosof24_leads.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-3 text-white sm:px-8">
        <div className="flex items-center gap-2">
          <button data-testid="leads-back" onClick={() => nav("/centrale")} className="rounded-full bg-slate-800 p-2"><ArrowLeft className="h-4 w-4" /></button>
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0E7C7B]"><Radio className="h-4 w-4" /></div>
          <p className="font-display text-sm font-extrabold">ProntoSof24 · Lead B2B</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="leads-settings-btn" onClick={() => nav("/centrale/b2b")} className="rounded-full bg-slate-800 px-3 py-2 text-xs font-bold">Impostazioni sito</button>
          <button data-testid="leads-export" onClick={exportCsv} className="flex items-center gap-1 rounded-full bg-[#0E7C7B] px-3 py-2 text-xs font-bold"><Download className="h-4 w-4" />CSV</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-8">
        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Totale lead" value={stats.total} />
            <Stat label="Nuovi" value={stats.new} />
            <Stat label="Analisi fissate" value={stats.analisi} />
            <Stat label="Proposte inviate" value={stats.proposte} />
            <Stat label="Firmati" value={stats.firmati} />
            <Stat label="Unità attivate" value={stats.units_active} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, società, email, codice" className="w-56 text-sm outline-none" />
          </div>
          <select data-testid="leads-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="">Tutti gli stati</option>
            {LEAD_STATUS_ORDER.map((s) => <option key={s} value={s}>{LEAD_STATUS[s].label}</option>)}
          </select>
        </div>

        {leads === null && <p className="text-sm text-slate-400">Caricamento…</p>}
        {leads?.length === 0 && <p data-testid="leads-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">Nessun lead trovato.</p>}

        <div className="space-y-2">
          {leads?.map((l) => {
            const st = LEAD_STATUS[l.status] || LEAD_STATUS.nuovo;
            return (
              <button key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => nav(`/centrale/lead/${l.id}`)}
                className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-[#0E7C7B]">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs font-bold text-[#0E7C7B]">{l.code}</span>
                    <span className="font-bold text-slate-900">{l.name}</span>
                    {l.company && <span className="text-xs text-slate-400">· {l.company}</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{l.role || "—"} · {l.units || "?"} unità · {l.zones || "zona n/d"} · ricevuto {fmt(l.created_at)}</p>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold ${st.cls}`}>{st.label}</span>
                <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
